import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import ProductEnquiryForm from "./ProductEnquiryForm";

/**
 * ProductPageShell — the shared layout for Operon Campus / Panels / Sprint.
 * Hero → 3 "what you get" bullets → optional visual → enquiry form. Siblings
 * share the Operon navy/cyan DNA; `accent` tints kickers/CTAs only.
 *
 * Props:
 *   product   "campus" | "panels" | "sprint"
 *   label     "Operon Campus" etc.
 *   accent    tailwind colour name (amber|emerald|violet|teal|cyan)
 *   kicker    small line above the hero title
 *   title     hero h1
 *   lede      hero paragraph
 *   bullets   array of 3 { title, body }
 *   visual    optional React node (right of hero on desktop)
 */
export default function ProductPageShell({
  product, label, accent, kicker, title, lede, bullets, visual, secondaryCta,
}) {
  const navigate = useNavigate();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollToForm = () => {
    const el = document.getElementById("request-access");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const accentClasses = {
    cyan:    { chip: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5", cta: "bg-cyan-500 hover:bg-cyan-400", chipDot: "text-cyan-400" },
    amber:   { chip: "text-amber-300 border-amber-500/30 bg-amber-500/5", cta: "bg-amber-500 hover:bg-amber-400", chipDot: "text-amber-400" },
    emerald: { chip: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5", cta: "bg-emerald-500 hover:bg-emerald-400", chipDot: "text-emerald-400" },
    violet:  { chip: "text-violet-300 border-violet-500/30 bg-violet-500/5", cta: "bg-violet-500 hover:bg-violet-400", chipDot: "text-violet-400" },
    teal:    { chip: "text-teal-300 border-teal-500/30 bg-teal-500/5", cta: "bg-teal-500 hover:bg-teal-400", chipDot: "text-teal-400" },
  }[accent] || { chip: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5", cta: "bg-cyan-500 hover:bg-cyan-400", chipDot: "text-cyan-400" };

  return (
    <div className="min-h-screen bg-[#060B1C] text-[#E9EEF9] font-sans overflow-x-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className={`absolute top-[-10%] left-[-10%] w-[45rem] h-[45rem] rounded-full bg-${accent}-500/10 blur-[120px]`} />
        <div className="absolute bottom-[-20%] right-[-10%] w-[42rem] h-[42rem] rounded-full bg-[#3E7AD6]/10 blur-[120px]" />
      </div>

      {/* Top nav */}
      <nav className="border-b border-[#243768]/50 bg-[#0B1735]/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="btn-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Operon</span>
          </button>
          <Button
            onClick={scrollToForm}
            className={`${accentClasses.cta} text-[#060B1C] font-semibold rounded-full px-5 py-2 h-auto`}
            data-testid={`${product}-nav-cta`}
          >
            Request access
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 md:pt-24 pb-14">
        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 items-center">
          <div>
            <div className={`inline-block text-[11px] tracking-[0.25em] font-mono uppercase mb-6 px-3 py-1 rounded-full border ${accentClasses.chip}`}>
              {kicker}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight mb-6">
              {title}
            </h1>
            <p className="text-lg text-[#9DB0D6] leading-relaxed max-w-2xl mb-8">
              {lede}
            </p>
            <div className="text-xs text-[#6C7EA6] font-mono mb-6">
              <span className={`${accentClasses.chipDot}`}>●</span> Private beta · Platforms with a person in the loop
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={scrollToForm}
                className={`${accentClasses.cta} text-[#060B1C] font-semibold rounded-full px-6 py-6 h-auto text-base`}
                data-testid={`${product}-hero-cta`}
              >
                Request access
              </Button>
              {secondaryCta && (
                <Button
                  onClick={secondaryCta.onClick}
                  variant="outline"
                  className="rounded-full px-6 py-6 h-auto text-base bg-transparent border-[#3A4F85] text-white hover:bg-white/5 hover:text-white"
                  data-testid={`${product}-secondary-cta`}
                >
                  {secondaryCta.label}
                </Button>
              )}
            </div>
          </div>

          {visual && (
            <div className="hidden lg:block">
              {visual}
            </div>
          )}
        </div>
      </section>

      {/* 3 bullets */}
      <section className="border-t border-[#243768]/50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-[10px] font-mono ${accentClasses.chipDot.replace('text-', 'text-')} tracking-[0.3em] uppercase mb-6`}>
            What you get
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {bullets.map((b, i) => (
              <div key={i} className="rounded-2xl border border-[#243768] bg-[#0B1735]/60 p-6 hover:border-[#57C7E6]/40 transition-colors">
                <div className={`text-3xl font-extrabold font-mono opacity-30 mb-2 ${accentClasses.chipDot}`}>0{i + 1}</div>
                <h3 className="text-white font-semibold text-lg mb-2">{b.title}</h3>
                <p className="text-[#9DB0D6] text-sm leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="request-access" className="border-t border-[#243768]/50 py-16 md:py-20">
        <div className="max-w-2xl mx-auto px-6">
          <ProductEnquiryForm product={product} productLabel={label} accent={accent} />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#243768]/50 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo-complete.png" alt="Operon" className="h-10 object-contain" />
          <div className="text-[10px] font-mono text-[#6C7EA6] tracking-[0.3em] uppercase">
            Platforms with a person in the loop
          </div>
        </div>
      </footer>
    </div>
  );
}
